import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InternalRefetchQueriesInclude, useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import React, { useMemo, useState } from "react";
import { ProductIdTypeEnum, ProductTypesEnum } from "__generated__/globalTypes";
import { CART_QUERY, CartQuery, useAddToCart, useRemoveFromCart } from "components/Cart/queries";
import Button, { ButtonLink } from "components/common/Button";
import CouponDiscount from "components/common/CouponDiscount";
import CreditHoursBadge from "components/common/CreditHoursBadge/CreditHoursBadge";
import Link from "components/common/Link";
import Loading from "components/common/Loading";
import { Price } from "components/common/Price/Price";
import {
  DiscountOptionsFromSubscription,
  useDiscountOptionsFromSubscription,
} from "components/common/Price/utils";
import {
  BOARD_APPROVAL_URL,
  BUNDLE_URL,
  CART_URL,
  COURSE_URL,
  MY_COURSES_URL,
  SUBSCRIPTIONS_URL,
} from "constants/urls";
import { getEnrollmentDetailsForCourse } from "helpers/courseDetails";
import { CURRENT_USER_QUERY } from "hooks/useUserContext";
import { PurchaseCompleteProps } from "../Cart/CheckoutModal";
import ProductPurchaseModal from "../common/PurchaseModal/ProductPurchaseModal";
import { ProductBundleViewQueryProduct } from "./__generated__/ProductBundle";
import { COURSE_VIEW_QUERY } from "./queries";
import styles from "./ProductSidebar.module.css";

interface ProductSidebarProps extends PurchaseCompleteProps {
  product: Omit<
    ProductBundleViewQueryProduct,
    "productCategories" | "courseGroups" | "bundleFields"
  >;
  isMembersExclusive?: boolean;
}

interface ProductInfo {
  price: number;
  ceUnits: number;
  modalTitle: string;
  alreadyPurchasedText: JSX.Element | string;
  quickPurchaseText: string;
  type: "bundle" | "course";
  discountOptionsFromSubscription: DiscountOptionsFromSubscription;
  isMembersExclusive?: boolean;
}

const useProductInfo = (
  product: ProductSidebarProps["product"],
  selectedSubscriptionDiscountId?: string
): ProductInfo => {
  const isBundle = product.type === ProductTypesEnum.BUNDLE;
  const isCourse = product.type === ProductTypesEnum.SIMPLE && product.courses.length === 1;
  const discountOptionsFromSubscription = useDiscountOptionsFromSubscription(
    product,
    selectedSubscriptionDiscountId
  );

  const ceUnits = useMemo(() => {
    if (isBundle) {
      return (
        product.courses.reduce((units, course) => {
          return units + course.ceUnits;
        }, 0) || 0
      );
    }

    if (isCourse) {
      return product.courses[0].ceUnits;
    }

    throw new Error("Invalid product");
  }, [product.courses, isBundle, isCourse]);

  let modalTitle = "";
  let alreadyPurchasedText: JSX.Element | string = "";
  let quickPurchaseText = "";

  if (isBundle) {
    modalTitle = "Package Purchase";
    alreadyPurchasedText = "You have purchased this package.";
    quickPurchaseText = "Purchase this package";
  } else if (isCourse) {
    modalTitle = "Course Enrollment";
    quickPurchaseText = discountOptionsFromSubscription.isFree ? "Get Now" : "Order Now";

    const course = product.courses[0];
    const { messageText } = getEnrollmentDetailsForCourse(course);
    const courseUrl = makeUrl(COURSE_URL, {
      slug: course.slug as string,
      page: course.courseProgress.toLowerCase(),
    });
    // If we're showing this text, the user is not viewing the latest state of the course
    // and needs to reload the page. So, use an <a> tag to take them to the lessons.
    alreadyPurchasedText = (
      <>
        {messageText} <a href={courseUrl}>Go to lessons</a>
      </>
    );
  } else {
    throw new Error("Invalid product");
  }

  return {
    ceUnits,
    modalTitle,
    alreadyPurchasedText,
    quickPurchaseText,
    type: isBundle ? "bundle" : "course",
    price: product.priceUSD,
    discountOptionsFromSubscription,
  };
};

const BundleCourseList = ({ bundle }: { bundle: ProductSidebarProps["product"] }) => (
  <>
    <h3 className="font-bold text-xl">Courses</h3>
    <ul className={styles.bundleCourses}>
      {bundle?.courses.map(course => (
        <li key={course.id}>
          <Link href={makeUrl(COURSE_URL, { slug: course.slug as string, page: "" })}>
            {course.title}
          </Link>
        </li>
      ))}
    </ul>
  </>
);

const ProductSidebar: React.FC<ProductSidebarProps> = ({
  product,
  onPurchaseComplete,
  isMembersExclusive,
}) => {
  const addToCart = useAddToCart();
  const removeFromCart = useRemoveFromCart();
  const [activeSubscriptionDiscountId, setActiveSubscriptionDiscountId] = useState<
    string | undefined
  >();
  const { data: cartData, loading: loadingCart } = useEnhancedQuery<CartQuery>(CART_QUERY);

  const { isInCart, cartParentBundle } = useMemo(() => {
    const cartItem = cartData?.cart?.contents?.nodes?.find(
      n => n?.product?.node?.id === product.id
    );

    const bundleKey = cartItem?.extraData?.find(d => d?.key === "bundled_by")?.value;
    const bundle = cartData?.cart?.contents?.nodes?.find(n => n?.key === bundleKey);

    return {
      isInCart: Boolean(cartItem),
      cartParentBundle: bundle,
    };
  }, [cartData?.cart?.contents?.nodes, product.id]);

  const {
    price,
    ceUnits,
    quickPurchaseText,
    alreadyPurchasedText,
    modalTitle,
    type,
    discountOptionsFromSubscription,
  } = useProductInfo(product, activeSubscriptionDiscountId);

  const hasPriceOrCreditValue = price || (product?.canPurchaseWithCredits && product?.creditCost);
  const isAvailableToCustomer =
    !isMembersExclusive ||
    (isMembersExclusive && discountOptionsFromSubscription.isFreeOrDiscounted);
  const { activeDiscountFromSubscription } = discountOptionsFromSubscription;
  const canPurchase =
    hasPriceOrCreditValue && product?.availability?.isPurchasable && isAvailableToCustomer;
  const tokensText = canPurchase && product?.creditCost ? `${product?.creditCost} tokens` : null;

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [addToCartDisabled, setAddToCartDisabled] = useState(false);
  const onClickPurchase = () => setPaymentModalOpen(true);
  const onCloseModal = () => setPaymentModalOpen(false);

  const refetchQueries: InternalRefetchQueriesInclude = useMemo(() => {
    return [
      { query: CURRENT_USER_QUERY },
      ...product.courses.map(course => ({
        query: COURSE_VIEW_QUERY,
        variables: {
          id: course.id,
          idType: ProductIdTypeEnum.ID,
        },
      })),
    ];
  }, [product.courses]);

  return (
    <>
      <ProductPurchaseModal
        productId={product?.id}
        productDatabaseId={product?.databaseId}
        productName={product?.name}
        productPrice={product?.priceUSD}
        hasPurchased={Boolean(product?.hasPurchased)}
        canPurchaseWithCredits={Boolean(product?.canPurchaseWithCredits)}
        creditCost={product?.creditCost}
        subscriptionDiscount={activeDiscountFromSubscription}
        type={type}
        open={paymentModalOpen}
        onRequestClose={onCloseModal}
        onPurchaseComplete={onPurchaseComplete}
        title={modalTitle}
        redirectUrl={MY_COURSES_URL}
        refetchQueries={refetchQueries}
      />
      <div className={styles.root} role="complementary">
        <div className={styles.wrapper}>
          <CreditHoursBadge ceUnits={ceUnits} />
          <div className={styles.priceWrapper}>
            {canPurchase ? (
              <Price
                price={price}
                discountOptions={discountOptionsFromSubscription}
                discountOnChange={setActiveSubscriptionDiscountId}
                tokensText={tokensText}
              />
            ) : (
              <div>
                {isMembersExclusive ? (
                  <span>
                    <Link href={makeUrl(SUBSCRIPTIONS_URL, { slug: "" })}>Members Only</Link>
                    <br />
                    <br />
                    If you are subscribed, please log in to continue.
                  </span>
                ) : (
                  <span>Not available for purchase</span>
                )}
              </div>
            )}
            {product.couponForUser && <CouponDiscount couponForUser={product.couponForUser} />}
            {loadingCart ? (
              <Loading />
            ) : (
              <div className="my-4">
                {product.hasPurchased && (
                  <div className="text-base py-5">{alreadyPurchasedText}</div>
                )}
                {isInCart && !cartParentBundle && (
                  <>
                    <ButtonLink href={CART_URL} theme="primary" className="my-2 w-full">
                      Proceed to checkout
                    </ButtonLink>
                    <Button
                      theme="dangerInverse"
                      icon={<FontAwesomeIcon icon={faTrash} className="mr-1" />}
                      onClick={() => {
                        removeFromCart({ id: product.id });
                        setAddToCartDisabled(false);
                      }}
                      className="m-auto"
                    >
                      Remove from cart
                    </Button>
                  </>
                )}
                {cartParentBundle && (
                  <div className="text-base py-5">
                    This item is in your <Link href={CART_URL}>cart</Link> via{" "}
                    <Link
                      href={makeUrl(BUNDLE_URL, {
                        slug: cartParentBundle.product?.node?.slug || "",
                      })}
                    >
                      {cartParentBundle.product?.node?.name}
                    </Link>
                    .
                  </div>
                )}
                {!product.hasPurchased && !isInCart && canPurchase && (
                  <>
                    <Button theme="primary" className="w-full" onClick={onClickPurchase}>
                      {quickPurchaseText}
                    </Button>
                    {!discountOptionsFromSubscription.isFree && (
                      <Button
                        theme="textButton"
                        className="mt-2 w-full"
                        onClick={() => {
                          setAddToCartDisabled(true);
                          const extra = discountOptionsFromSubscription.isFreeOrDiscounted
                            ? JSON.stringify({
                                subscriptionDiscount: activeDiscountFromSubscription,
                              })
                            : undefined;
                          return addToCart(product.databaseId, extra);
                        }}
                        disabled={addToCartDisabled}
                      >
                        Add to cart
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div>
            {product.type === ProductTypesEnum.BUNDLE && (
              <div className="mb-4">
                <BundleCourseList bundle={product} />
              </div>
            )}

            {!!ceUnits && (
              <div className="text-center">
                <div className="font-bold">
                  Zur Institute is <Link href={BOARD_APPROVAL_URL}>Board Approved</Link> for:
                </div>
                Psychologists, LMFTs, SWs, Counselors, Nurses & More
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductSidebar;
